import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';

const GOLD = '#d4af37';

interface BeachItem {
  id: string;
  name: string;
  rating: string;
  reviews: number;
  type: string;
  location: string;
  mapUrl: string;
  imageUrl?: string;
  distance?: string;
  description?: string;
}

interface BeachesContent {
  title: string;
  description: string;
  beaches: BeachItem[];
}

const BeachesNearbyManager = () => {
  const [content, setContent] = useState<BeachesContent>({
    title: 'Beaches Nearby',
    description: 'Explore beautiful beaches around Muchukunnu',
    beaches: [
      { id: '1', name: 'Thikkodi Drive-in Beach', rating: '4.5', reviews: 745, type: 'Drive-in Beach', location: 'Thikkodi, Kerala', mapUrl: 'https://maps.app.goo.gl/cYlu6oDMVovpOI3RU', imageUrl: '', distance: '7.1 km', description: 'Unique beach where vehicles can drive near shore. Distance: 7.1 km, Travel time: 18 minutes. Route: Muchukunnu → Purakkad Road → Thikkodi.' },
      { id: '2', name: 'Payyoli Beach', rating: '4.1', reviews: 152, type: 'Beach', location: 'Payyoli, Kerala', mapUrl: 'https://maps.app.goo.gl/payyoli', imageUrl: '' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingBeachId, setUploadingBeachId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const beachFileInputRef = useRef<HTMLInputElement>(null);
  const pendingBeachId = useRef<string | null>(null);
  const beachFocusHandler = useRef<(() => void) | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'beaches'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as BeachesContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'nearby_content', 'beaches'), content);
      showSnackbar('Beaches data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddBeach = () => {
    const newBeach: BeachItem = {
      id: Date.now().toString(),
      name: 'New Beach',
      rating: '4.0',
      reviews: 0,
      type: 'Beach',
      location: '',
      mapUrl: '',
      imageUrl: '',
    };
    setContent({
      ...content,
      beaches: [...content.beaches, newBeach]
    });
  };

  const handleBeachChange = (id: string, field: keyof BeachItem, value: any) => {
    setContent({
      ...content,
      beaches: content.beaches.map(beach =>
        beach.id === id ? { ...beach, [field]: value } : beach
      )
    });
  };

  const handleDeleteBeach = (id: string) => {
    setContent({
      ...content,
      beaches: content.beaches.filter(beach => beach.id !== id)
    });
  };

  // ─── Auto-compress image to base64 ───
  const compressImage = (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.92): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        let base64 = canvas.toDataURL('image/jpeg', quality);
        let q = quality;
        while (base64.length > 900_000 && q > 0.5) {
          q -= 0.05;
          base64 = canvas.toDataURL('image/jpeg', q);
        }
        if (base64.length > 900_000) {
          const smallCanvas = document.createElement('canvas');
          const scale = 0.75;
          smallCanvas.width = Math.round(width * scale);
          smallCanvas.height = Math.round(height * scale);
          const sCtx = smallCanvas.getContext('2d')!;
          sCtx.imageSmoothingEnabled = true;
          sCtx.imageSmoothingQuality = 'high';
          sCtx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
          base64 = smallCanvas.toDataURL('image/jpeg', 0.8);
        }
        resolve(base64);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  // ─── Beach Image Upload (auto-compressed) ───
  const handleBeachFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setUploadingBeachId(id);
    setUploadProgress(10);
    try {
      setUploadProgress(30);
      const base64 = await compressImage(file);
      setUploadProgress(90);
      setContent({
        ...content,
        beaches: content.beaches.map(b =>
          b.id === id ? { ...b, imageUrl: base64 } : b
        )
      });
      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingBeachId(null);
        setUploadProgress(0);
        showSnackbar(`Image compressed (${originalKB}KB → ${compressedKB}KB) & uploaded! Click "Save Changes" to persist.`, 'success');
      }, 400);
    } catch {
      showSnackbar('Failed to process image', 'error');
      setUploadingBeachId(null);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Beaches Nearby Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage nearby beaches content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏖️ Section Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🌊 Beaches ({content.beaches.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddBeach}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Beach
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.beaches.map((beach) => (
            <Grid item xs={12} key={beach.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Beach Name"
                      value={beach.name}
                      onChange={(e) => handleBeachChange(beach.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      value={beach.type}
                      onChange={(e) => handleBeachChange(beach.id, 'type', e.target.value)}
                      size="small"
                      placeholder="e.g., Beach, Tourist attraction"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton
                      onClick={() => handleDeleteBeach(beach.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={beach.location}
                      onChange={(e) => handleBeachChange(beach.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Rating"
                      value={beach.rating}
                      onChange={(e) => handleBeachChange(beach.id, 'rating', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Reviews"
                      value={beach.reviews}
                      onChange={(e) => handleBeachChange(beach.id, 'reviews', parseInt(e.target.value) || 0)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Map URL"
                      value={beach.mapUrl || ''}
                      onChange={(e) => handleBeachChange(beach.id, 'mapUrl', e.target.value)}
                      size="small"
                      placeholder="https://maps.app.goo.gl/..."
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Distance (Optional)"
                      value={beach.distance || ''}
                      onChange={(e) => handleBeachChange(beach.id, 'distance', e.target.value)}
                      size="small"
                      placeholder="e.g., 1.5 km"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={beach.description || ''}
                      onChange={(e) => handleBeachChange(beach.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={beach.imageUrl?.startsWith('data:') ? '(Uploaded image)' : beach.imageUrl || ''}
                      onChange={(e) => handleBeachChange(beach.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={beach.imageUrl?.startsWith('data:')}
                      placeholder="https://example.com/beach-image.jpg"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingBeachId === beach.id}
                        onClick={() => {
                          pendingBeachId.current = beach.id;
                          beachFileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingBeachId.current === beach.id) {
                                pendingBeachId.current = null;
                              }
                              setUploadingBeachId((prev) => prev === beach.id ? null : prev);
                              setUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            beachFocusHandler.current = null;
                          };
                          beachFocusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD, color: GOLD, fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'none', height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingBeachId === beach.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {beach.imageUrl?.startsWith('data:') && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleBeachChange(beach.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingBeachId === beach.id && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {beach.imageUrl && (
                      <Box sx={{ width: '100%', height: 100, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={beach.imageUrl} alt={beach.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Hidden file input for beach images */}
      <input
        type="file"
        accept="image/*"
        ref={beachFileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (beachFocusHandler.current) {
            window.removeEventListener('focus', beachFocusHandler.current);
            beachFocusHandler.current = null;
          }
          const file = e.target.files?.[0];
          if (file && pendingBeachId.current) {
            handleBeachFileUpload(pendingBeachId.current, file);
          }
          pendingBeachId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BeachesNearbyManager;
